/**
 * Bundle 163 - Shared verdict chip + legend.
 *
 * Single source of truth for verdict rendering. Consumed by:
 *   - AdminIncompleteProfilesPage table column
 *   - AdminUserDetailPage SIGNUP INCOMPLETE chip
 *   - AdvancedDiagnosticLookup fallback readout
 *
 * Canonical descriptions match the backend `VERDICT_LEGEND`
 * response from /api/admin/verdict-legend. If we ever want to
 * localise, this is the file to swap for a t() call.
 *
 * WCAG-AA note: every colour pair below passes AA contrast at
 * 12 px + regular weight against a white or 50-tint background.
 */
import React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger }
  from '@/components/ui/tooltip';

export const VERDICTS = {
  LEAK: {
    label: 'LEAK',
    dot: 'bg-red-500',
    chip: 'bg-red-100 text-red-800 border-red-200',
    description: (
      'Missing DOB and no flag stamped. Compliance breach.'
      + ' Requires backfill or manual outreach.'
    ),
  },
  FLAGGED: {
    label: 'FLAGGED',
    dot: 'bg-amber-500',
    chip: 'bg-amber-100 text-amber-800 border-amber-200',
    description: (
      'Awaiting user to complete signup. Will be blocked at'
      + ' next login by CompleteSignupModal (Bundle 160). No'
      + ' admin action required unless they never return.'
    ),
  },
  LEGACY: {
    label: 'LEGACY',
    dot: 'bg-neutral-400',
    chip: 'bg-neutral-100 text-neutral-700 border-neutral-200',
    description: (
      'Pre-Bundle-149 (2026-08-31 cutoff) user. Grandfathered'
      + ' per policy. Not retro-blocked.'
    ),
  },
  COMPLETE: {
    label: 'COMPLETE',
    dot: 'bg-emerald-500',
    chip: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    description: (
      'All required signup data present. Row should not'
      + ' normally appear in this list.'
    ),
  },
};

export const VERDICT_ORDER = ['LEAK', 'FLAGGED', 'LEGACY', 'COMPLETE'];

/**
 * A single verdict chip.
 * @param {'LEAK'|'FLAGGED'|'LEGACY'|'COMPLETE'} verdict
 * @param {string} [prefix] - optional prefix e.g. 'SIGNUP INCOMPLETE'
 * @param {string} [testId]
 *
 * Tooltip strategy (Bundle 163b hotfix): native `title`
 * attribute is the source of truth so every browser + every
 * SSR path shows a tooltip on hover. The Radix TooltipContent
 * is layered on top for a nicer visual but is not required for
 * correctness.
 */
export const VerdictChip = ({ verdict, prefix, testId }) => {
  const meta = VERDICTS[verdict] || VERDICTS.LEGACY;
  const label = prefix ? `${prefix} - ${meta.label}` : meta.label;
  const tooltipCopy = `${meta.label}: ${meta.description}`;
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 '
              + 'text-[10px] font-semibold uppercase tracking-wide cursor-help '
              + meta.chip
            }
            data-testid={testId || `verdict-chip-${verdict}`}
            title={tooltipCopy}
            aria-label={tooltipCopy}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs" side="top">
          <div className="font-semibold mb-1">{meta.label}</div>
          <div>{meta.description}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Sticky legend row for the incomplete-profiles table.
 * Renders each of the 4 verdicts with a colour-swatch + one-
 * line description. Sticks to top-0 with a subtle backdrop so
 * it stays visible while the table scrolls.
 */
export const VerdictLegend = () => (
  <div
    className="sticky top-0 z-10 rounded-md border border-neutral-200 bg-white/95 backdrop-blur px-3 py-2"
    data-testid="verdict-legend"
  >
    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
      Verdict legend
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
      {VERDICT_ORDER.map((v) => {
        const meta = VERDICTS[v];
        return (
          <div
            key={v}
            className="flex items-start gap-2"
            data-testid={`verdict-legend-item-${v}`}
          >
            <span
              className={
                'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 '
                + 'text-[10px] font-semibold uppercase tracking-wide shrink-0 '
                + meta.chip
              }
            >
              <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
              {meta.label}
            </span>
            <span className="text-[11px] text-neutral-600 leading-tight">
              {meta.description}
            </span>
          </div>
        );
      })}
    </div>
  </div>
);
